import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, ackAlert, ackAllAlerts, getAlerts } from '../../services/api';
import { useAlertsSocket } from '../../services/useAlertsSocket';
import { useAuthStore } from '../../store/authStore';
import { parseUtc } from '../../utils/dataAge';
import './AlertsPanel.css';

// ── Labels ────────────────────────────────────────────────────────────────────

const ALERT_TYPE_LABEL: Record<string, string> = {
  zone_enter:     '➡️ Wjazd do strefy',
  zone_exit:      '⬅️ Wyjazd ze strefy',
  armed_movement: '🚨 Alarm antykradzieżowy',
};

const FILTER_TYPES: Array<{ value: string; label: string }> = [
  { value: '',               label: 'Wszystkie' },
  { value: 'zone_enter',     label: 'Wjazd' },
  { value: 'zone_exit',      label: 'Wyjazd' },
  { value: 'armed_movement', label: 'Alarm' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatRelative = (iso: string): string => {
  const diff = Date.now() - parseUtc(iso).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1)  return 'przed chwilą';
  if (m < 60) return `${m} min temu`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} h temu`;
  return parseUtc(iso).toLocaleString('pl-PL');
};

const playAlertSound = (type: string): void => {
  try {
    const ctx = new AudioContext();
    const osc  = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (type === 'armed_movement') {
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.setValueAtTime(660, ctx.currentTime + 0.18);
      osc.frequency.setValueAtTime(880, ctx.currentTime + 0.36);
      gain.gain.setValueAtTime(0.35, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.55);
      osc.start();
      osc.stop(ctx.currentTime + 0.55);
    } else {
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.2, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start();
      osc.stop(ctx.currentTime + 0.3);
    }
  } catch {
    // AudioContext blocked or unavailable — silently skip
  }
};

const showBrowserNotification = (alert: Alert): void => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
  const title = ALERT_TYPE_LABEL[alert.type] ?? alert.type;
  new Notification(title, {
    body: `${alert.device_name}${alert.message ? '\n' + alert.message : ''}`,
    icon: '/favicon.ico',
    tag:  `fleet-alert-${alert.id}`,
  });
};

// ── Component ─────────────────────────────────────────────────────────────────

const AlertsPanel: React.FC = () => {
  const { token } = useAuthStore();

  const [alerts,     setAlerts]     = useState<Alert[]>([]);
  const [open,       setOpen]       = useState(false);
  const [busy,       setBusy]       = useState(false);
  const [filterType, setFilterType] = useState('');
  const [filterUnack, setFilterUnack] = useState(false);

  // ── initial load ────────────────────────────────────────────────────────────
  useEffect(() => {
    getAlerts({ limit: 100 })
      .then(setAlerts)
      .catch(err => console.warn('[AlertsPanel] initial load failed', err));
  }, []);

  // ── WebSocket: new alerts ───────────────────────────────────────────────────
  const onNewAlert = useCallback((alert: Alert) => {
    setAlerts(prev => {
      if (prev.some(a => a.id === alert.id)) return prev;   // deduplicate
      playAlertSound(alert.type);
      showBrowserNotification(alert);
      return [alert, ...prev];
    });
  }, []);

  const wsConnected = useAlertsSocket(token, onNewAlert);

  // ── browser notification permission ────────────────────────────────────────
  const openDrawer = () => {
    setOpen(true);
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  };

  // ── filtered list ───────────────────────────────────────────────────────────
  const filtered = useMemo(() => alerts.filter(a => {
    if (filterType  && a.type !== filterType)  return false;
    if (filterUnack && a.acknowledged)          return false;
    return true;
  }), [alerts, filterType, filterUnack]);

  const unackCount = useMemo(() => alerts.filter(a => !a.acknowledged).length, [alerts]);

  // ── ack single ──────────────────────────────────────────────────────────────
  const handleAck = async (id: number) => {
    setBusy(true);
    try {
      const updated = await ackAlert(id);
      setAlerts(prev => prev.map(a => a.id === id ? updated : a));
    } finally {
      setBusy(false);
    }
  };

  // ── ack all ─────────────────────────────────────────────────────────────────
  const handleAckAll = async () => {
    setBusy(true);
    try {
      await ackAllAlerts();
      const now = new Date().toISOString();
      setAlerts(prev => prev.map(a =>
        a.acknowledged ? a : { ...a, acknowledged: true, ack_at: now },
      ));
    } finally {
      setBusy(false);
    }
  };

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <button
        className={`nav-item alerts-trigger${unackCount > 0 ? ' has-unack' : ''}`}
        onClick={openDrawer}
      >
        Alerty
        {unackCount > 0 && <span className="badge">{unackCount}</span>}
      </button>

      {open && (
        <>
          <div className="alerts-backdrop" onClick={() => setOpen(false)} />
          <aside className="alerts-drawer" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <header className="drawer-head">
              <div className="drawer-head-left">
                <span className={`ws-dot ${wsConnected ? 'ws-on' : 'ws-off'}`}
                      title={wsConnected ? 'Live' : 'Połączenie…'} />
                <h3>
                  Alerty
                  {unackCount > 0 && <span> ({unackCount} nieprzeczytanych)</span>}
                </h3>
              </div>
              <div className="drawer-head-right">
                {unackCount > 0 && (
                  <button className="ack-all-btn" onClick={handleAckAll} disabled={busy}>
                    Potwierdź wszystkie
                  </button>
                )}
                <button onClick={() => setOpen(false)} className="close-btn" title="Zamknij">✕</button>
              </div>
            </header>

            {/* Filter bar */}
            <div className="filter-bar">
              <div className="filter-chips">
                {FILTER_TYPES.map(f => (
                  <button
                    key={f.value}
                    className={`chip${filterType === f.value ? ' chip-active' : ''}`}
                    onClick={() => setFilterType(f.value)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <label className="unack-toggle">
                <input
                  type="checkbox"
                  checked={filterUnack}
                  onChange={e => setFilterUnack(e.target.checked)}
                />
                Niepotwierdzone
              </label>
            </div>

            {/* Alert list */}
            <div className="drawer-body">
              {filtered.length === 0 && (
                <div className="empty">
                  {alerts.length === 0 ? 'Brak alertów' : 'Brak wyników dla wybranych filtrów'}
                </div>
              )}
              {filtered.map(a => (
                <div key={a.id} className={`alert-item${a.acknowledged ? ' acked' : ''}${a.type === 'armed_movement' ? ' alarm' : ''}`}>
                  <div className="alert-head">
                    <span className="type">{ALERT_TYPE_LABEL[a.type] ?? a.type}</span>
                    <span className="time">{formatRelative(a.fixtime)}</span>
                  </div>
                  <div className="device">{a.device_name}</div>
                  {a.geofence_name && <div className="zone">Strefa: {a.geofence_name}</div>}
                  {a.message       && <div className="message">{a.message}</div>}
                  {!a.acknowledged && (
                    <button onClick={() => handleAck(a.id)} disabled={busy} className="ack-btn">
                      {busy ? '…' : 'Potwierdź'}
                    </button>
                  )}
                </div>
              ))}
            </div>

          </aside>
        </>
      )}
    </>
  );
};

export default AlertsPanel;
